import { useCallback, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  isV2Path,
  toLegacyEquivalentPath,
  toV2EquivalentPath,
  V2_PATHS,
} from "../../universal/v2Routes";
import {
  respondToUiExperimentPrompt,
  switchUiDesignLane,
} from "../../universal/uiExperiments";

function mergeUiExperimentFields(user, fields) {
  if (!user || !fields) {
    return user;
  }

  return {
    ...user,
    uiV2Eligible: fields.uiV2Eligible ?? user.uiV2Eligible,
    uiV2PromptState: fields.uiV2PromptState ?? user.uiV2PromptState,
    uiDesignPreference: fields.uiDesignPreference ?? user.uiDesignPreference,
  };
}

export default function useUiDesignActions({ user, actions, onComplete }) {
  const history = useHistory();
  const location = useLocation();
  const [pending, setPending] = useState(false);

  const refreshUser = useCallback(
    async (fields) => {
      if (fields && user) {
        actions?.setUser?.({
          user: mergeUiExperimentFields(user, fields),
        });
      }
      await actions?.refreshSessionUser?.();
      onComplete?.();
    },
    [actions, onComplete, user]
  );

  const acceptPrompt = useCallback(async () => {
    setPending(true);
    try {
      const result = await respondToUiExperimentPrompt("accept");
      await refreshUser(result);
      history.push(V2_PATHS.lobby);
    } finally {
      setPending(false);
    }
  }, [history, refreshUser]);

  const declinePrompt = useCallback(async () => {
    setPending(true);
    try {
      const result = await respondToUiExperimentPrompt("decline");
      await refreshUser(result);
    } finally {
      setPending(false);
    }
  }, [refreshUser]);

  const switchToV2 = useCallback(async () => {
    setPending(true);
    try {
      const result = await switchUiDesignLane("v2");
      await refreshUser(result);
      const targetPath = toV2EquivalentPath(location.pathname) || V2_PATHS.lobby;
      if (targetPath !== location.pathname) {
        history.push(targetPath);
      }
    } finally {
      setPending(false);
    }
  }, [history, location.pathname, refreshUser]);

  const switchToLegacy = useCallback(async () => {
    setPending(true);
    try {
      const result = await switchUiDesignLane("legacy");
      await refreshUser(result);
      const targetPath = toLegacyEquivalentPath(location.pathname) || "/lobby";
      if (targetPath !== location.pathname) {
        history.push(targetPath);
      }
    } finally {
      setPending(false);
    }
  }, [history, location.pathname, refreshUser]);

  const isEligible = user?.uiV2Eligible === true;
  const promptPending = user?.uiV2PromptState === "pending";
  const onV2Lane = isV2Path(location.pathname);
  const showSwitchToV2 = isEligible && !onV2Lane;
  const showSwitchToLegacy = isEligible && onV2Lane;

  return {
    pending,
    isEligible,
    promptPending,
    onV2Lane,
    showSwitchToV2,
    showSwitchToLegacy,
    acceptPrompt,
    declinePrompt,
    switchToV2,
    switchToLegacy,
  };
}
